import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { DialogData, DialogService } from '@eforall/dashboard';
import { AdmissionsMatchingFuncData, AdmissionsMatchingMessage } from '@interfaces';
import { NgxJsonViewerModule } from 'ngx-json-viewer';
import { FuncService, TrimesterService } from '../../../../../services';
import { AdmissionsManagerService } from '../../admissions-manager.service';
import { MatcherDialogData } from './matcher/dialog/matcher-dialog-data';
import { MatcherDialog } from './matcher/dialog/matcher.dialog';


@Component({
	selector: 'admissions-manager-trimester-switchover-view-part',
	standalone: true,
	imports: [CommonModule, NgxJsonViewerModule],
	templateUrl: './trimester-switchover-view.part.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ['./trimester-switchover-view.part.scss'],
})
export class AdmissionsTrimesterSwitchoverViewPart {

	private readonly func = inject(FuncService);
	public readonly pageService = inject(AdmissionsManagerService);
	private readonly dialogService = inject(DialogService);
	private readonly trimesterService = inject(TrimesterService);

	public readonly funcData = signal<AdmissionsMatchingFuncData | undefined>(undefined);

	constructor() {
		toObservable(this.trimesterService.selectedTrimester).subscribe(async (selectedTrimester) => {
			const funcData = this.funcData();
			if (funcData == undefined) return;
			if (selectedTrimester?.trimesterId !== funcData.trimesterId) this.funcData.set(undefined);
		});
	}


	public async load() {
		const trimesterId = this.trimesterService.selectedTrimester()?.trimesterId;
		if (!trimesterId) return;

		const data = await this.func.admissions.matching.getData({ trimesterId })


		this.funcData.set(data);
	}


	public readonly loaded = computed(() => {
		const funcData = this.funcData();
		return (funcData !== undefined);
	});


	public readonly loadLabel = computed(() => {
		if (this.loaded()) return 'Reload';
		else return 'Load';
	});


	public readonly height = computed(() => {

		const layout = this.pageService.layout();
		if (!layout) return 0;

		return layout.tab1ContentHeight - layout.tab1ViewDroplistHeight;

	});


	public readonly messages = computed<AdmissionsMatchingMessage[]>(() => {
		const funcData = this.funcData();
		if (funcData) return funcData.messages;
		else return [
			{ type: 'Info', message: `Click the Load Button to continue.` }
		];
	});


	public readonly worstMessageType = computed<'Info' | 'Warning' | 'Error' | 'Critical'>(() => {
		const messages = this.messages();
		if (messages.find(m => m.type == 'Critical')) return 'Critical';
		if (messages.find(m => m.type == 'Error')) return 'Error';
		if (messages.find(m => m.type == 'Warning')) return 'Warning';
		return 'Info';
	});


	public readonly showRunButtons = computed(() => {
		if (this.funcData() == undefined) return false;
		return true;
	});


	public async run(run: 'Test' | 'Live') {

		const funcData = this.funcData();
		if (!funcData) return;

		const worst = this.worstMessageType();

		if (run == 'Live') {
			if (worst == 'Critical' || worst == 'Error') {
				this.dialogService.showMessage('Cannot Run Matching', `You cannot perform a Live Run until all the errors are resolved.`, 'Okay', [], 300, 200);
				return;
			}
		}
		else {
			if (worst == 'Critical') {
				this.dialogService.showMessage('Cannot Run Matching', `You cannot perform a Test Run until the Critical errors are resolved.`, 'Okay', [], 300, 200);
				return;
			}
		}

		const matchData: DialogData<MatcherDialogData> = {
			data: { run, data: funcData },
		};

		const action = await this.dialogService.showCustom(
			MatcherDialog,
			matchData,
			1200, 800);

		// Data was applied and saved to the DB , clearing the local copy
		if (action.id === 'apply') {
			this.funcData.set(undefined);
		}
	}


	getIconClass(type: 'Info' | 'Warning' | 'Error' | 'Critical'): string {

		if (type == 'Info') return 'fa-circle-info';
		if (type == 'Warning') return 'fa-triangle-exclamation';
		if (type == 'Error') return 'fa-circle-xmark';
		if (type == 'Critical') return 'fa-square-x';
		return 'fa-circle';
	}

}